if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isCarousel = this.dataset.desktopLayout === 'carousel';
        if (this.isCarousel) {
          this.elements.viewer.addEventListener('keydown', this.onCarouselKeydown.bind(this));
          requestAnimationFrame(() => this.setActiveMedia(this.dataset.initialMediaId, false, true));
        }
        if (this.isCarousel || this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        }
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.isCarousel) {
          const media = event.detail.currentElement;
          if (!media) return;
          this.setCarouselCurrentMedia(media);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            event.detail.currentPage
          );
        }
        if (!this.elements.thumbnails || !event.detail.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onCarouselKeydown(event) {
        const slider = this.elements.viewer.slider;
        if (event.target !== slider) return;
        const slides = Array.from(slider.querySelectorAll('li[data-media-id]'));
        const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
        const targetIndex = {
          ArrowLeft: Math.max(0, index - 1),
          ArrowRight: Math.min(slides.length - 1, index + 1),
          Home: 0,
          End: slides.length - 1,
        }[event.key];
        if (targetIndex === undefined || !slides[targetIndex]) return;
        event.preventDefault();
        this.setActiveMedia(slides[targetIndex].dataset.mediaId, false);
      }

      setCarouselCurrentMedia(activeMedia) {
        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((media) => {
          const active = media === activeMedia;
          media.classList.toggle('is-active', active);
          media.inert = !active;
          if (active) media.removeAttribute('aria-hidden');
          else media.setAttribute('aria-hidden', 'true');
        });
        this.playActiveMedia(activeMedia);
      }

      // Native smooth scrolling is slow across several slides, so animate a short ease-out instead.
      scrollCarouselTo(left, instant) {
        const slider = this.elements.viewer.slider;
        const resetSliderStyles = () => {
          slider.style.scrollSnapType = '';
          slider.style.scrollBehavior = '';
        };
        cancelAnimationFrame(this.carouselAnimationFrame);

        const start = slider.scrollLeft;
        const distance = left - start;
        if (instant || !distance || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          resetSliderStyles();
          slider.scrollTo({ left, behavior: 'instant' });
          return;
        }

        const duration = 250;
        const startTime = performance.now();
        slider.style.scrollSnapType = 'none';
        slider.style.scrollBehavior = 'auto';
        const step = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          slider.scrollLeft = start + distance * (1 - Math.pow(1 - progress, 3));
          if (progress < 1) {
            this.carouselAnimationFrame = requestAnimationFrame(step);
          } else {
            resetSliderStyles();
          }
        };
        this.carouselAnimationFrame = requestAnimationFrame(step);
      }

      setActiveMedia(mediaId, prepend, instant = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (this.isCarousel) {
          this.setCarouselCurrentMedia(activeMedia);
          const firstMedia = this.elements.viewer.slider.querySelector('li[data-media-id]');
          this.scrollCarouselTo(activeMedia.offsetLeft - firstMedia.offsetLeft, instant);
          this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`));
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
